import React from 'react';
import { Table } from 'ui';
import { ConfigProvider } from 'antd';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { OrderLayout } from './style';
import { useNavigate } from 'react-router-dom';
import useFarmOrderHandler from 'hooks/useFarmOrderHandler';
import { useBreakpoint } from 'utils/useBreakpoint';
import PublicRoute from 'enums/publicRoutes';

interface MyOrdersResponse {
  id: number;
  order_id: string;
  farm_id: number;
  transactions: {
    id: number;
    order_id: number;
    farm_id: number;
    status: string;
  };
}

export const MyOrders = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { handleDownloadClick, pdfLoading } = useFarmOrderHandler();
  const { isLgDown } = useBreakpoint();

  const isSmallScreen = isLgDown;

  const columns = [
    {
      title: intl.formatMessage({ id: 'orders.order' }),
      dataIndex: 'number',
    },
    {
      title: intl.formatMessage({ id: 'general.date' }),
      dataIndex: 'created_at',
      render: (value: string) => value && dayjs(value).format('DD.MM.YYYY'),
    },
    {
      title: intl.formatMessage({ id: 'orders.statuss' }),
      render: (value: any) => {
        return value.transactions.map((transaction: any, index: number) => (
          <div key={index}>
            {intl.formatMessage({
              id: `enum.${transaction.status.toUpperCase()}`,
            }) +
              ' - ' +
              (transaction?.farm?.brand ?? transaction?.farm?.name ?? '')}
          </div>
        ));
      },
    },
    {
      title: intl.formatMessage({ id: 'orders.summary' }),
      dataIndex: 'total',
      render: (value: string, row: any) =>
        `€ ${value} ${intl.formatMessage({ id: 'general.for' })} ${row.product_count} ` +
        (row.product_count > 1
          ? intl.formatMessage({ id: 'orders.for_units' })
          : intl.formatMessage({ id: 'orders.for_unit' })),
    },
    {
      title: intl.formatMessage({ id: 'orders.actions' }),
      dataIndex: 'transactions',
      render: (values: MyOrdersResponse[], record: any) => {
        const firstOrderId = values?.at(0)?.order_id;
        return (
        <>
          {record.status !== 'proccess' && (
            <div className="order-actions">
              {firstOrderId && (
                <span
                  className={'view-order'}
                  onClick={() =>
                    navigate(
                      `${PublicRoute.USER_ORDER}?order_id=${firstOrderId}&date=${dayjs(record.created_at).format('DD.MM.YYYY')}`
                    )
                  }
                >
                  {intl.formatMessage({ id: 'orders.view' })}
                </span>
              )}
              <span
                className="order-download no-row-click"
                onClick={(e) => {
                  e.stopPropagation();
                  const validOrders = values
                    .filter((value) => value && value.order_id && value.farm_id)
                    .map((value) => ({
                      order_id: value.order_id,
                      farm_id: value.farm_id,
                    }));

                  handleDownloadClick(validOrders);
                }}
              >
                {intl.formatMessage({ id: 'orders.download' })}
              </span>
            </div>
          )}
        </>
        );
      },
    },
  ];

  return (
    <OrderLayout>
      <h2 className="title sub-title">
        {intl.formatMessage({ id: 'general.my_orders' })}
      </h2>
      <ConfigProvider
        theme={{
          token: {
            borderRadius: 0,
            colorBorderSecondary: 'none',
          },
        }}
      >
        <Table
          loading={pdfLoading}
          url="/api/v2/user/order"
          columns={columns}
          className="order-table"
          onRow={(record: any) => {
            if (!isSmallScreen) return {};
            return {
              onClick: (event: React.MouseEvent<HTMLTableRowElement>) => {
                const target = event.target as HTMLElement;

                if (target.closest('.no-row-click')) {
                  return;
                }

                const text = target.textContent?.trim();
                if (!text) {
                  return;
                }

                const order_id = Array.isArray(record.transactions)
                  ? record.transactions[0]?.order_id
                  : record.order_id;

                if (!order_id) {
                  return;
                }

                const order_date = dayjs(record.created_at).format(
                  'DD.MM.YYYY'
                );

                navigate(`${PublicRoute.USER_ORDER}?order_id=${order_id}&date=${order_date}`);
              },
              style: { cursor: 'pointer' },
            };
          }}
        />
      </ConfigProvider>
    </OrderLayout>
  );
};
